import React, { Component } from 'react';
import { AuthContext } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import LoadingSpinner from '../components/LoadingSpinner';
import api from '../services/api';

class Account extends Component {
  static contextType = AuthContext;

  constructor(props) {
    super(props);
    this.state = {
      activeTab: 'profile',
      
      // Profile form
      profileForm: {
        firstName: '',
        lastName: '',
        company: '',
        email: '',
      },
      
      // Password form
      passwordForm: {
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      },
      
      // ✅ Add notification preferences
      notificationPreferences: [],
      loadingPreferences: true,
      
      // UI state
      loading: true,
      profileErrors: {},
      passwordErrors: {},
      profileSuccess: null,
      passwordSuccess: null,
      preferencesSuccess: null, // ✅ Add this
      isSubmitting: false,
      
      // Account info
      accountInfo: null,
    };
  }

  async componentDidMount() {
    await this.loadUserData();
    await this.loadNotificationPreferences(); // ✅ Add this
  }

  loadUserData = async () => {
    try {
      const response = await api.get('/auth/me');
      const user = response.data.user;
      
      this.setState({
        profileForm: {
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          company: user.company || '',
          email: user.email || '',
        },
        accountInfo: {
          createdAt: user.createdAt,
          admin: user.admin,
          id: user.id,
        },
        loading: false,
      });
    } catch (error) {
      console.error('Failed to load user data:', error);
      this.setState({ loading: false });
    }
  };

  handleTabChange = (tab) => {
    this.setState({ 
      activeTab: tab,
      profileSuccess: null,
      passwordSuccess: null,
      profileErrors: {},
      passwordErrors: {},
    });
  };

  handleProfileChange = (e) => {
    const { name, value } = e.target;
    this.setState({
      profileForm: { ...this.state.profileForm, [name]: value },
      profileErrors: { ...this.state.profileErrors, [name]: '' },
      profileSuccess: null,
    });
  };

  handlePasswordChange = (e) => {
    const { name, value } = e.target;
    this.setState({
      passwordForm: { ...this.state.passwordForm, [name]: value },
      passwordErrors: { ...this.state.passwordErrors, [name]: '' },
      passwordSuccess: null,
    });
  };

  validateProfileForm = () => {
    const { firstName, lastName, email } = this.state.profileForm;
    const errors = {};

    if (!firstName.trim()) {
      errors.firstName = 'First name is required';
    } else if (firstName.trim().length < 2) {
      errors.firstName = 'First name must be at least 2 characters';
    }

    if (!lastName.trim()) {
      errors.lastName = 'Last name is required';
    } else if (lastName.trim().length < 2) {
      errors.lastName = 'Last name must be at least 2 characters';
    }

    if (!email) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = 'Email is invalid';
    }

    return errors;
  };

  validatePasswordForm = () => {
    const { currentPassword, newPassword, confirmPassword } = this.state.passwordForm;
    const errors = {};

    if (!currentPassword) {
      errors.currentPassword = 'Current password is required';
    }

    if (!newPassword) {
      errors.newPassword = 'New password is required';
    } else if (newPassword.length < 8) {
      errors.newPassword = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(newPassword)) {
      errors.newPassword = 'Password must contain uppercase, lowercase, and number';
    }

    if (!confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (newPassword !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    return errors;
  };

  handleProfileSubmit = async (e) => {
    e.preventDefault();

    const errors = this.validateProfileForm();
    if (Object.keys(errors).length > 0) {
      this.setState({ profileErrors: errors });
      return;
    }

    this.setState({ isSubmitting: true, profileSuccess: null });

    try {
      const { firstName, lastName, company, email } = this.state.profileForm;
      
      const response = await api.put('/auth/profile', {
        firstName,
        lastName,
        company,
        email,
      });

      // Update context with new user data
      const updatedUser = response.data.user;
      localStorage.setItem('user', JSON.stringify(updatedUser));

      this.setState({
        isSubmitting: false,
        profileSuccess: 'Profile updated successfully',
      });

      // Reload page to update navbar
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      this.setState({
        isSubmitting: false,
        profileErrors: { 
          general: error.response?.data?.message || 'Failed to update profile' 
        },
      });
    }
  };

  handlePasswordSubmit = async (e) => {
    e.preventDefault();

    const errors = this.validatePasswordForm();
    if (Object.keys(errors).length > 0) {
      this.setState({ passwordErrors: errors });
      return;
    }

    this.setState({ isSubmitting: true, passwordSuccess: null });

    try {
      const { currentPassword, newPassword } = this.state.passwordForm;
      
      await api.put('/auth/password', {
        currentPassword,
        newPassword,
      });

      this.setState({
        isSubmitting: false,
        passwordSuccess: 'Password changed successfully',
        passwordForm: {
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        },
      });
    } catch (error) {
      this.setState({
        isSubmitting: false,
        passwordErrors: { 
          general: error.response?.data?.message || 'Failed to change password' 
        },
      });
    }
  };

  formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  loadNotificationPreferences = async () => {
    try {
      const response = await api.get('/notifications/preferences');
      
      // ✅ Filter out job-related preferences (now handled by subscriptions)
      const nonJobPreferences = response.data.preferences.filter(pref => 
        !['job_shared', 'job_created', 'job_updated', 'job_deleted'].includes(pref.type)
      );
      
      this.setState({
        notificationPreferences: nonJobPreferences,
        loadingPreferences: false,
      });
    } catch (error) {
      console.error('Failed to load notification preferences:', error);
      this.setState({ loadingPreferences: false });
    }
  };
  
  handlePreferenceToggle = async (preferenceType, currentValue) => {
    try {
      await api.put('/notifications/preferences', {
        type: preferenceType,
        enabled: !currentValue,
      });
  
      // Update local state
      this.setState(prevState => ({
        notificationPreferences: prevState.notificationPreferences.map(pref =>
          pref.type === preferenceType
            ? { ...pref, enabled: !currentValue }
            : pref
        ),
        preferencesSuccess: 'Preferences updated successfully',
      }));
  
      // Clear success message after 3 seconds
      setTimeout(() => {
        this.setState({ preferencesSuccess: null });
      }, 3000);
    } catch (error) {
      console.error('Failed to update preference:', error);
    }
  };
  
  getPreferenceLabel = (type) => {
    const labels = {
      auto_watch_created_jobs: 'Auto-watch jobs I create',
      auto_watch_shared_jobs: 'Auto-watch jobs shared with me',
      user_invited: 'User Invited',
      invitation_accepted: 'Invitation Accepted',
      user_registered: 'New User Registered',
      password_changed: 'Password Changed',
      email_changed: 'Email Changed',
    };
  
    return labels[type] || type;
  };
  
  getPreferenceDescription = (type) => {
    const descriptions = {
      auto_watch_created_jobs: 'Automatically subscribe to notifications when you create a job',
      auto_watch_shared_jobs: 'Automatically subscribe to notifications when a job is shared with you',
      user_invited: 'Get notified when you invite a user (admins only)',
      invitation_accepted: 'Get notified when your invitation is accepted',
      user_registered: 'Get notified when a new user registers (admins only)',
      password_changed: 'Get notified when your password is changed',
      email_changed: 'Get notified when your email is changed',
    };
  
    return descriptions[type] || '';
  };


  render() {
    const { 
      activeTab, 
      profileForm, 
      passwordForm, 
      notificationPreferences,
      loadingPreferences,
      loading, 
      profileErrors, 
      passwordErrors,
      profileSuccess,
      passwordSuccess,
      preferencesSuccess,
      isSubmitting,
      accountInfo,
    } = this.state;

    if (loading) {
      return (
        <div className="min-h-screen bg-slate-50">
          <Navbar />
          <div className="flex items-center justify-center h-96">
            <LoadingSpinner size="large" />
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />

        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-slate-900">My Account</h1>
            <p className="text-slate-600 mt-1">Manage your account settings and preferences</p>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="border-b border-slate-200">
              <nav className="flex -mb-px">
                <button
                  onClick={() => this.handleTabChange('profile')}
                  className={`px-6 py-4 text-sm font-medium border-b-2 transition ${
                    activeTab === 'profile'
                      ? 'border-teal-500 text-teal-600'
                      : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                  }`}
                >
                  <span className="flex items-center space-x-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span>Profile</span>
                  </span>
                </button>

                <button
                  onClick={() => this.handleTabChange('security')}
                  className={`px-6 py-4 text-sm font-medium border-b-2 transition ${
                    activeTab === 'security'
                      ? 'border-teal-500 text-teal-600'
                      : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                  }`}
                >
                  <span className="flex items-center space-x-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    <span>Security</span>
                  </span>
                </button>
                <button
                  onClick={() => this.handleTabChange('preferences')}
                  className={`px-6 py-4 text-sm font-medium border-b-2 transition ${
                    activeTab === 'preferences'
                      ? 'border-teal-500 text-teal-600'
                      : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                  }`}
                >
                  <span className="flex items-center space-x-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                    <span>Preferences</span>
                  </span>
                </button>
                <button
                  onClick={() => this.handleTabChange('info')}
                  className={`px-6 py-4 text-sm font-medium border-b-2 transition ${
                    activeTab === 'info'
                      ? 'border-teal-500 text-teal-600'
                      : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                  }`}
                >
                  <span className="flex items-center space-x-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Account Info</span>
                  </span>
                </button>
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {/* Profile Tab */}
              {activeTab === 'profile' && (
                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-4">Profile Information</h2>
                  <p className="text-sm text-slate-600 mb-6">
                    Update your account profile information and email address.
                  </p>

                  {profileSuccess && (
                    <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
                      {profileSuccess}
                    </div>
                  )}

                  {profileErrors.general && (
                    <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                      {profileErrors.general}
                    </div>
                  )}

                  <form onSubmit={this.handleProfileSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="firstName" className="block text-sm font-medium text-slate-700 mb-2">
                          First Name
                        </label>
                        <input
                          id="firstName"
                          name="firstName"
                          type="text"
                          value={profileForm.firstName}
                          onChange={this.handleProfileChange}
                          className={`input-field ${profileErrors.firstName ? 'border-red-500' : ''}`}
                          disabled={isSubmitting}
                        />
                        {profileErrors.firstName && (
                          <p className="error-text">{profileErrors.firstName}</p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="lastName" className="block text-sm font-medium text-slate-700 mb-2">
                          Last Name
                        </label>
                        <input
                          id="lastName"
                          name="lastName"
                          type="text"
                          value={profileForm.lastName}
                          onChange={this.handleProfileChange}
                          className={`input-field ${profileErrors.lastName ? 'border-red-500' : ''}`}
                          disabled={isSubmitting}
                        />
                        {profileErrors.lastName && (
                          <p className="error-text">{profileErrors.lastName}</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                        Email Address
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        value={profileForm.email}
                        onChange={this.handleProfileChange}
                        className={`input-field ${profileErrors.email ? 'border-red-500' : ''}`}
                        disabled={isSubmitting}
                      />
                      {profileErrors.email && (
                        <p className="error-text">{profileErrors.email}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-slate-700 mb-2">
                        Company
                      </label>
                      <input
                        id="company"
                        name="company"
                        type="text"
                        value={profileForm.company}
                        onChange={this.handleProfileChange}
                        className="input-field"
                        disabled={isSubmitting}
                      />
                    </div>

                    <div className="pt-4">
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="btn-primary"
                      >
                        {isSubmitting ? (
                          <span className="flex items-center">
                            <LoadingSpinner size="small" />
                            <span className="ml-2">Saving...</span>
                          </span>
                        ) : (
                          'Save Changes'
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Security Tab */}
              {activeTab === 'security' && (
                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-4">Change Password</h2>
                  <p className="text-sm text-slate-600 mb-6">
                    Ensure your account is using a long, random password to stay secure.
                  </p>

                  {passwordSuccess && (
                    <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
                      {passwordSuccess}
                    </div>
                  )}

                  {passwordErrors.general && (
                    <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                      {passwordErrors.general}
                    </div>
                  )}

                  <form onSubmit={this.handlePasswordSubmit} className="space-y-5 max-w-md">
                    <div>
                      <label htmlFor="currentPassword" className="block text-sm font-medium text-slate-700 mb-2">
                        Current Password
                      </label>
                      <input
                        id="currentPassword"
                        name="currentPassword"
                        type="password"
                        autoComplete="current-password"
                        value={passwordForm.currentPassword}
                        onChange={this.handlePasswordChange}
                        className={`input-field ${passwordErrors.currentPassword ? 'border-red-500' : ''}`}
                        disabled={isSubmitting}
                      />
                      {passwordErrors.currentPassword && (
                        <p className="error-text">{passwordErrors.currentPassword}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="newPassword" className="block text-sm font-medium text-slate-700 mb-2">
                        New Password
                      </label>
                      <input
                        id="newPassword"
                        name="newPassword"
                        type="password"
                        autoComplete="new-password"
                        value={passwordForm.newPassword}
                        onChange={this.handlePasswordChange}
                        className={`input-field ${passwordErrors.newPassword ? 'border-red-500' : ''}`}
                        disabled={isSubmitting}
                      />
                      {passwordErrors.newPassword && (
                        <p className="error-text">{passwordErrors.newPassword}</p>
                      )}
                      <p className="mt-1 text-xs text-slate-500">
                        Must be at least 8 characters with uppercase, lowercase, and number
                      </p>
                    </div>

                    <div>
                      <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-700 mb-2">
                        Confirm New Password
                      </label>
                      <input
                        id="confirmPassword"
                        name="confirmPassword"
                        type="password"
                        autoComplete="new-password"
                        value={passwordForm.confirmPassword}
                        onChange={this.handlePasswordChange}
                        className={`input-field ${passwordErrors.confirmPassword ? 'border-red-500' : ''}`}
                        disabled={isSubmitting}
                      />
                      {passwordErrors.confirmPassword && (
                        <p className="error-text">{passwordErrors.confirmPassword}</p>
                      )}
                    </div>

                    <div className="pt-4">
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="btn-primary"
                      >
                        {isSubmitting ? (
                          <span className="flex items-center">
                            <LoadingSpinner size="small" />
                            <span className="ml-2">Updating...</span>
                          </span>
                        ) : (
                          'Update Password'
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Account Info Tab */}
              {activeTab === 'info' && accountInfo && (
                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-4">Account Information</h2>
                  <p className="text-sm text-slate-600 mb-6">
                    View your account details and status.
                  </p>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between py-4 border-b border-slate-200">
                      <div>
                        <p className="text-sm font-medium text-slate-900">User ID</p>
                        <p className="text-sm text-slate-500 mt-1">Your unique account identifier</p>
                      </div>
                      <p className="text-sm text-slate-700 font-mono">#{accountInfo.id}</p>
                    </div>

                    <div className="flex items-center justify-between py-4 border-b border-slate-200">
                      <div>
                        <p className="text-sm font-medium text-slate-900">Account Role</p>
                        <p className="text-sm text-slate-500 mt-1">Your access level</p>
                      </div>
                      <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                        accountInfo.admin 
                          ? 'bg-purple-100 text-purple-800' 
                          : 'bg-slate-100 text-slate-800'
                      }`}>
                        {accountInfo.admin ? 'Admin' : 'User'}
                      </span>
                    </div>

                    <div className="flex items-center justify-between py-4 border-b border-slate-200">
                      <div>
                        <p className="text-sm font-medium text-slate-900">Member Since</p>
                        <p className="text-sm text-slate-500 mt-1">When you joined PlanSource</p>
                      </div>
                      <p className="text-sm text-slate-700">{this.formatDate(accountInfo.createdAt)}</p>
                    </div>

                    <div className="flex items-center justify-between py-4">
                      <div>
                        <p className="text-sm font-medium text-slate-900">Email Status</p>
                        <p className="text-sm text-slate-500 mt-1">Your email verification status</p>
                      </div>
                      <span className="px-3 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                        Verified
                      </span>
                    </div>
                  </div>
                </div>
              )}
              {/* Preferences Tab */}
              {activeTab === 'preferences' && (
                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-4">Notification Preferences</h2>
                  <p className="text-sm text-slate-600 mb-6">
                    Manage your global notification settings. For job-specific notifications, use the watch feature on individual jobs.
                  </p>

                  {preferencesSuccess && (
                    <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
                      {preferencesSuccess}
                    </div>
                  )}

                  {loadingPreferences ? (
                    <div className="flex items-center justify-center py-12">
                      <LoadingSpinner size="large" />
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* ✅ Auto-Subscribe Settings Section */}
                      <div>
                        <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center">
                          <svg className="w-5 h-5 mr-2 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          Auto-Watch Settings
                        </h3>
                        <div className="space-y-3">
                          {notificationPreferences
                            .filter(pref => pref.type.startsWith('auto_watch'))
                            .map((pref) => (
                              <div
                                key={pref.type}
                                className="flex items-start justify-between p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition"
                              >
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-slate-900">
                                    {this.getPreferenceLabel(pref.type)}
                                  </p>
                                  <p className="text-xs text-slate-500 mt-1">
                                    {this.getPreferenceDescription(pref.type)}
                                  </p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer ml-4">
                                  <input
                                    type="checkbox"
                                    checked={pref.enabled}
                                    onChange={() => this.handlePreferenceToggle(pref.type, pref.enabled)}
                                    className="sr-only peer"
                                  />
                                  <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600"></div>
                                </label>
                              </div>
                            ))}
                        </div>
                      </div>

                      {/* ✅ Account Notifications Section */}
                      <div>
                        <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center">
                          <svg className="w-5 h-5 mr-2 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          Account Notifications
                        </h3>
                        <div className="space-y-3">
                          {notificationPreferences
                            .filter(pref => !pref.type.startsWith('auto_watch'))
                            .map((pref) => (
                              <div
                                key={pref.type}
                                className="flex items-start justify-between p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition"
                              >
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-slate-900">
                                    {this.getPreferenceLabel(pref.type)}
                                  </p>
                                  <p className="text-xs text-slate-500 mt-1">
                                    {this.getPreferenceDescription(pref.type)}
                                  </p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer ml-4">
                                  <input
                                    type="checkbox"
                                    checked={pref.enabled}
                                    onChange={() => this.handlePreferenceToggle(pref.type, pref.enabled)}
                                    className="sr-only peer"
                                  />
                                  <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600"></div>
                                </label>
                              </div>
                            ))}
                        </div>
                      </div>

                      {/* ✅ Link to Subscriptions */}
                      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-start">
                          <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-blue-900">
                              Want to customize notifications for specific jobs?
                            </p>
                            <p className="text-xs text-blue-700 mt-1">
                              Use the watch button on individual jobs to control which job notifications you receive.
                            </p>
                            <button
                              onClick={() => window.location.href = '/subscriptions'}
                              className="mt-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
                            >
                              Manage job subscriptions →
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    );
  }
}

export default Account;